import React, { useEffect, useMemo, useState } from "react";
import { ArrowBackIcon } from "../../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../../util/SubMenu";

const KILOMETRES_PER_MILE = 1.609;

const TRAINING_ZONES = [
    { name: "Zone 1", fasterFactor: 0.675, slowerFactor: 0.601941747572816 },
    { name: "Zone 2", fasterFactor: 0.76, slowerFactor: 0.675 },
    { name: "Zone 3", fasterFactor: 0.89, slowerFactor: 0.761 },
    { name: "Zone 4", fasterFactor: 1.04854368932039, slowerFactor: 0.891 },
    { name: "Zone 5", fasterFactor: 1.1747572815534, slowerFactor: 1.04854368932039 },
];

const RACE_ESTIMATES = [
    { name: "5K", distance: 5, displayDistance: 5, factor: 1.083 },
    { name: "10K", distance: 10, displayDistance: 10, factor: 1.124 },
    { name: "21K", distance: 21.1, displayDistance: 21, factor: 1.178 },
    { name: "42K", distance: 42.2, displayDistance: 42, factor: 1.225 },
];

function formatPace(minutes) {
    const totalSeconds = Math.max(0, Math.round(minutes * 60));
    const wholeMinutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${wholeMinutes}:${String(seconds).padStart(2, "0")}`;
}

function formatRaceTime(minutes) {
    const totalSeconds = Math.max(0, Math.round(minutes * 60));
    const hours = Math.floor(totalSeconds / 3600);
    const remainingSeconds = totalSeconds % 3600;
    const wholeMinutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${hours}:${String(wholeMinutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function calculateRaceEstimates(testPaceMinutes) {
    return RACE_ESTIMATES.map((race) => {
        const finishTimeMinutes = testPaceMinutes * race.distance * race.factor;

        return {
            ...race,
            finishTime: formatRaceTime(finishTimeMinutes),
            pacePerKilometre: formatPace(finishTimeMinutes / race.displayDistance),
        };
    });
}

function calculateTrainingZones(testPaceMinutes) {
    const zones = TRAINING_ZONES.map((zone) => ({
        name: zone.name,
        fasterKm: testPaceMinutes / zone.fasterFactor,
        slowerKm: testPaceMinutes / zone.slowerFactor,
    }));

    return zones.map((zone) => ({
        ...zone,
        fasterMile: zone.fasterKm * KILOMETRES_PER_MILE,
        slowerMile: zone.slowerKm * KILOMETRES_PER_MILE,
    }));
}

export default function SixMinutePaceCalculator() {
    const [minutes, setMinutes] = useState(6);
    const [seconds, setSeconds] = useState(0);
    const [showTestHelp, setShowTestHelp] = useState(false);

    useEffect(() => {
        document.title = "6 Minute Pace Calculator - MCC";
    }, []);

    useEffect(() => {
        if (!showTestHelp) return undefined;

        const closeOnEscape = (event) => {
            if (event.key === "Escape") setShowTestHelp(false);
        };

        window.addEventListener("keydown", closeOnEscape);
        return () => window.removeEventListener("keydown", closeOnEscape);
    }, [showTestHelp]);

    const testPaceMinutes = minutes + seconds / 60;
    const inputError = seconds < 0 || seconds >= 60
        ? "Enter seconds from 0 to 59."
        : testPaceMinutes <= 0
            ? "Enter a pace greater than 0:00."
            : "";

    const raceEstimates = useMemo(
        () => inputError ? [] : calculateRaceEstimates(testPaceMinutes),
        [inputError, testPaceMinutes]
    );
    const trainingZones = useMemo(
        () => inputError ? [] : calculateTrainingZones(testPaceMinutes),
        [inputError, testPaceMinutes]
    );

    return (
        <div className="min-h-screen bg-white px-2 py-8 md:px-4">
            <div className="mx-auto max-w-4xl">
                <SubMenuWrapper>
                    <SubMenuItem
                        icon={<ArrowBackIcon size={24} color="#6b7280" />}
                        label="Training Plans"
                        to="/training-plans"
                    />
                </SubMenuWrapper>

                <header className="mb-7 mt-4 text-center">
                    <span
                        className="mb-3 inline-block rounded-full px-4 py-1 text-sm font-medium text-white"
                        style={{ background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)" }}
                    >
                        ⏱️ Momentum Members
                    </span>
                    <h1
                        className="mb-3 text-3xl font-extrabold md:text-4xl"
                        style={{
                            background: "linear-gradient(90deg, #1e293b 0%, #334155 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        6 Minute Pace Calculator
                    </h1>
                    <p className="mx-auto max-w-2xl text-base md:text-lg" style={{ color: "#64748b" }}>
                        Enter your average pace from the 6-minute test to estimate race times and training zones.
                    </p>
                </header>

                <main className="space-y-6">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                        <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <h2 className="text-xl font-bold text-slate-800">Your 6-minute test pace</h2>
                            <button
                                type="button"
                                onClick={() => setShowTestHelp(true)}
                                aria-haspopup="dialog"
                                aria-expanded={showTestHelp}
                                className="rounded-md text-xs font-semibold text-orange-700 underline underline-offset-2 hover:text-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-300"
                            >
                                How to
                            </button>
                        </div>
                        <p className="mb-5 text-sm text-slate-600">
                            Use minutes and seconds per kilometre. Results update as you change either value.
                        </p>

                        <div className="grid gap-4 sm:grid-cols-2 sm:max-w-lg">
                            <label className="block text-sm font-semibold text-slate-700">
                                Minutes
                                <input
                                    aria-label="Pace minutes"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={minutes}
                                    onChange={(event) => setMinutes(Math.max(0, Number(event.target.value) || 0))}
                                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-lg text-slate-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
                                />
                            </label>
                            <label className="block text-sm font-semibold text-slate-700">
                                Seconds
                                <input
                                    aria-label="Pace seconds"
                                    type="number"
                                    min="0"
                                    max="59"
                                    step="1"
                                    value={seconds}
                                    onChange={(event) => setSeconds(Math.max(0, Number(event.target.value) || 0))}
                                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-lg text-slate-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
                                />
                            </label>
                        </div>

                        {inputError && <p role="alert" className="mt-3 text-sm font-medium text-red-600">{inputError}</p>}
                    </section>

                    {showTestHelp && (
                        <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
                            onClick={() => setShowTestHelp(false)}
                        >
                            <section
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="six-minute-test-help-title"
                                className="relative max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowTestHelp(false)}
                                    aria-label="Close test instructions"
                                    className="absolute right-4 top-4 rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                >
                                    Close
                                </button>
                                <h2 id="six-minute-test-help-title" className="mb-3 pr-16 text-xl font-bold text-slate-800">
                                    How to perform the 6-minute test
                                </h2>
                                <p className="mb-4 text-sm leading-6 text-slate-600">
                                    Watch this video to learn how to perform it. Then come back and use the calculator to input your average pace for the test.
                                </p>
                                <a
                                    href="https://www.youtube.com/watch?v=MVOh2aFhiaA&t=1s"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex rounded-lg bg-orange-600 px-4 py-2 font-semibold text-white no-underline hover:bg-orange-700"
                                >
                                    Watch this video
                                </a>
                            </section>
                        </div>
                    )}

                    {!inputError && (
                        <>
                            <section aria-labelledby="training-zones-heading" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                                <div className="mb-4">
                                    <h2 id="training-zones-heading" className="text-xl font-bold text-slate-800">Training pace zones</h2>
                                    <p className="text-sm text-slate-600">Pace ranges are shown in minutes per kilometre and minutes per mile.</p>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                                        <caption className="sr-only">Training pace zones by kilometre and mile</caption>
                                        <thead>
                                            <tr className="border-b border-slate-200 text-slate-600">
                                                <th rowSpan="2" scope="col" className="px-3 py-3 font-semibold">Pace zone</th>
                                                <th colSpan="2" scope="colgroup" className="px-3 py-3 text-center font-semibold">Minutes per km</th>
                                                <th colSpan="2" scope="colgroup" className="px-3 py-3 text-center font-semibold">Minutes per mile</th>
                                            </tr>
                                            <tr className="border-b border-slate-200 text-xs text-slate-500">
                                                <th scope="col" className="px-3 pb-3 text-right font-medium">Faster</th>
                                                <th scope="col" className="px-3 pb-3 text-right font-medium">Slower</th>
                                                <th scope="col" className="px-3 pb-3 text-right font-medium">Faster</th>
                                                <th scope="col" className="px-3 pb-3 text-right font-medium">Slower</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trainingZones.map((zone) => (
                                                <tr
                                                    key={zone.name}
                                                    className={`border-b border-slate-100 last:border-0 ${
                                                        zone.name === "Zone 2"
                                                            ? "bg-green-100"
                                                            : zone.name === "Zone 5"
                                                                ? "bg-orange-100"
                                                                : ""
                                                    }`}
                                                >
                                                    <th scope="row" className="px-3 py-3 font-semibold text-slate-800">{zone.name}</th>
                                                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{formatPace(zone.fasterKm)}</td>
                                                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{formatPace(zone.slowerKm)}</td>
                                                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{formatPace(zone.fasterMile)}</td>
                                                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{formatPace(zone.slowerMile)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <section aria-labelledby="race-estimates-heading" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                                <div className="mb-4">
                                    <h2 id="race-estimates-heading" className="text-xl font-bold text-slate-800">Race time estimates</h2>
                                    <p className="text-sm text-slate-600">Estimated finish time and average pace.</p>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                    {raceEstimates.map((race) => (
                                        <article key={race.name} className="rounded-xl border border-orange-100 bg-orange-50 p-4">
                                            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-600">{race.name}</h3>
                                            <p className="m-0 text-2xl font-extrabold text-slate-900">{race.finishTime}</p>
                                            <p className="mt-1 text-sm text-slate-600">{race.pacePerKilometre} /km</p>
                                        </article>
                                    ))}
                                </div>
                            </section>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}
